// Shared constants and helpers for the marketing and programmatic-SEO pages.

import type { Country } from "./countries";

// The canonical origin used for absolute URLs (canonical tags, sitemap,
// structured data). A deployment pins it at build time; the fallback is used
// for local builds and CI.
const CONFIGURED_SITE = import.meta.env.VITE_SITE_URL as string | undefined;
export const SITE_URL = (
  CONFIGURED_SITE && CONFIGURED_SITE.length > 0 ? CONFIGURED_SITE : "https://doc.cheap"
).replace(/\/+$/, "");

// Where the documentation lives: `/docs` on this same origin, which is the
// path the contract publishes (`DEFAULT_DOCS_BASE_URL` in packages/contracts)
// and the address every `docs_url` in an error body points at. It is a
// relative path because it is the same host: the documentation is served from
// a static directory in front of this app, not from a site of its own, and a
// search engine reads its text as text on this origin rather than on a
// subdomain it ranks separately. A deployment that serves it somewhere else
// sets VITE_DOCS_URL.
export const DOCS_URL = (import.meta.env.VITE_DOCS_URL as string | undefined) ?? "/docs";

// The public sandbox key printed in the docs and the copy-paste examples. It
// carries no account and nothing to spend — its recognitions are free, capped
// by a lifetime allowance and rate limited per IP — so it is safe in static
// HTML. A live or session key must never appear here.
export const SANDBOX_PUBLIC_KEY = "sk_sandbox_public";

// The API host shown in the copy-paste curl. The public API origin, matching
// the landing demo snippet and the OpenAPI `servers` block.
export const API_HOST = "https://api.doc.cheap";

/** Absolute URL for a site-relative path. */
export function absoluteUrl(path: string): string {
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/** The path of a country's passport-OCR page. */
export function countryPath(country: Country): string {
  return `/${country.slug}-passport-ocr`;
}

// The example zones a country page publishes, and the holder they are built
// around, live in ./zones — they are the page's content rather than the site's
// addressing.
